import { notification } from 'antd'
import { NamePath } from 'antd/es/form/interface'
import { FormInstance } from 'antd/lib/form'

/**
 * Hoist nested envelope errors (e.g. `{ rca: { operating_mode: [...] } }`) up
 * to the top level so they map to flat form fields (`operating_mode`).
 */
const hoistEnvelopeKeys = (errorData: unknown, unwrapKeys?: string[]) => {
  if (
    !unwrapKeys?.length ||
    typeof errorData !== 'object' ||
    errorData === null ||
    Array.isArray(errorData)
  ) {
    return errorData
  }

  let result = errorData as Record<string, unknown>

  for (const key of unwrapKeys) {
    const nested = result[key]

    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const rest = { ...result }
      delete rest[key]
      result = { ...rest, ...(nested as Record<string, unknown>) }
    }
  }

  return result
}

/**
 * Show API errors in a notification
 * @param error - The error to show
 * @param form - The form to set the fields
 * @param options.unwrapKeys - Envelope keys (e.g. `rca`, `green_card`) whose
 * nested errors should be hoisted to the top level so they map to flat form
 * fields instead of dotted paths like `rca.operating_mode`.
 */
export const showApiErrors = async (
  error: unknown,
  form?: FormInstance,
  options?: { unwrapKeys?: string[] }
) => {
  let errorData: unknown = undefined

  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response: unknown }).response === 'object' &&
    (error as { response: unknown }).response !== null &&
    'data' in (error as { response: { data: unknown } }).response
  ) {
    errorData = (error as { response: { data: unknown } }).response.data
    if (errorData instanceof Blob) {
      try {
        const response = await errorData.text()
        errorData = JSON.parse(response)
      } catch (e) {
        console.error(e)
      }
    }
  } else if (typeof error === 'object' && error !== null && 'body' in error) {
    errorData = (error as { body: unknown }).body
  }

  errorData = hoistEnvelopeKeys(errorData, options?.unwrapKeys)

  const fieldErrors: { name: NamePath; errors: string[] }[] = []
  const notificationMessages: string[] = []

  if (typeof errorData === 'string') {
    notificationMessages.push(errorData)
  } else if (Array.isArray(errorData)) {
    notificationMessages.push(...errorData)
  } else if (typeof errorData === 'object' && errorData !== null) {
    const flattenErrors = (
      obj: Record<string, unknown>,
      prefix = ''
    ): Record<string, string[]> => {
      const flattened: Record<string, string[]> = {}

      for (const [key, value] of Object.entries(obj)) {
        const fullKey = prefix ? `${prefix}.${key}` : key

        if (Array.isArray(value)) {
          const messages = value.filter(
            (msg): msg is string => typeof msg === 'string'
          )
          if (messages.length > 0) {
            flattened[fullKey] = messages
          }

          value.forEach((item, index) => {
            if (typeof item === 'object' && item !== null) {
              const nested = flattenErrors(
                item as Record<string, unknown>,
                `${fullKey}[${index}]`
              )
              Object.assign(flattened, nested)
            }
          })
        } else if (typeof value === 'string') {
          flattened[fullKey] = [value]
        } else if (typeof value === 'object' && value !== null) {
          const nested = flattenErrors(
            value as Record<string, unknown>,
            fullKey
          )
          Object.assign(flattened, nested)
        }
      }

      return flattened
    }

    const flattenedErrors = flattenErrors(errorData as Record<string, unknown>)

    const registeredFieldKeys = new Set(
      (form?.getFieldsError?.() ?? []).map(({ name }) => JSON.stringify(name))
    )

    for (const [field, messages] of Object.entries(flattenedErrors)) {
      if (!messages.length) continue

      const capitalizedField = field.split('.').pop() || field
      const displayField =
        capitalizedField.charAt(0).toUpperCase() + capitalizedField.slice(1)

      const fieldPath = field
        .replace(/\[(\d+)\]/g, '.$1')
        .split('.')
        .map((part) => (part.match(/^\d+$/) ? Number(part) : part))

      const fieldExists =
        registeredFieldKeys.has(JSON.stringify(fieldPath)) ||
        form?.getFieldValue?.(fieldPath) !== undefined ||
        form?.getFieldInstance?.(fieldPath) !== undefined

      if (fieldExists) {
        fieldErrors.push({ name: fieldPath, errors: messages })
      } else {
        messages.forEach((msg) => {
          notificationMessages.push(`${displayField}: ${msg}`)
        })
      }
    }
  }

  if (form && fieldErrors.length) {
    form.setFields(fieldErrors)
  }

  const shown = new Set<string>()
  for (const msg of notificationMessages) {
    if (!shown.has(msg)) {
      shown.add(msg)
      notification.error({
        message: msg,
        duration: 2
      })
    }
  }
}
